import { ResourceArticle } from '../../types';

export const ARTICLES_PART_3: ResourceArticle[] = [
  {
    id: 'guide-21-elderly-protection',
    slug: 'protecting-elderly-parents-from-digital-scams',
    title: 'Protecting Elderly Parents & Seniors from Identity Theft and Cyber Fraud in Australia',
    category: 'Scams',
    date: '08 July 2026',
    author: {
      name: 'Sarah Chen',
      role: 'Consumer Rights Specialist, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 8,
    imageUrl: 'https://images.unsplash.com/photo-1581579438747-1dc8d17bbce4?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Supporting senior Australians with compassionate, non-judgmental cyber safety protocols and family monitoring.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Configuring simplified security settings and call-screening tools on seniors’ mobile devices.',
    summary: 'Australian seniors are disproportionately targeted by telemarketing fraud, fake government calls, and remote access scams. Learn how to safeguard your elderly parents without stripping their autonomy.',
    keyTakeaways: [
      'Scammers exploit seniors’ politeness, unfamiliarity with modern digital threats, and fear of government penalties (ATO/Centrelink).',
      'The most common attacks on elderly Australians are remote access tech support scams, romance fraud, and bogus investment schemes.',
      'Set up call-screening filters (such as Apple Silence Unknown Callers or Google Pixel Call Screen) on parents’ smartphones.',
      'Enroll aging relatives into the IDFraud.au Family Protection Circle to detect credential breaches on their behalf.',
      'Maintain an empathetic, blame-free environment so seniors feel safe disclosing mistakes immediately.'
    ],
    content: [
      'According to the ACCC, Australians over the age of 65 suffer the highest per-capita financial losses from scams and identity fraud of any demographic group.',
      'Seniors often possess substantial lifetime assets (superannuation payouts, paid-off homes, and retirement savings) while navigating an increasingly complex, digitized world of two-factor authentication, online banking, and automated government portals.',
      'In this practical guide, we provide a compassionate, respectful strategy for adult children to protect their aging parents from digital predators.'
    ],
    sections: [
      {
        heading: 'The 3 Most Dangerous Scam Vectors Targeting Australian Seniors',
        body: [
          '1. Remote Computer Access: Scammers call claiming to be Telstra, NBN, or Microsoft, telling the senior their computer has errors and convincing them to install TeamViewer or AnyDesk.',
          '2. "Hi Mum / Dad" Impersonation: Fraudsters text from unknown numbers pretending to be a son or daughter in distress requesting emergency money.',
          '3. Government Impersonation (Centrelink & ATO): Intimidating calls threatening loss of the age pension or immediate arrest unless gift cards or PayID transfers are sent.'
        ]
      },
      {
        heading: 'Practical Tech Tweaks to Safeguard Parents\' Devices',
        body: [
          'Take 15 minutes to configure these essential settings on your parents\' smartphones and computers:',
          'Enable "Silence Unknown Callers": On iPhone (Settings > Phone > Silence Unknown Callers) or Android Call Screen. This sends calls from numbers not in their contact book straight to voicemail, filtering out 95% of robocalls.',
          'Install an Ad-Blocker: Browser ad-blockers (such as uBlock Origin) prevent misleading pop-ups claiming "Your computer is infected with 5 viruses, call this 1800 number immediately".',
          'Set up Password Management & Biometrics: Enable Touch ID or Face ID so parents do not have to write passwords on physical paper notes near their computers.'
        ]
      }
    ],
    actionSteps: [
      'Configure spam call-blocking on your parents’ mobile phones.',
      'Establish a secret family emergency safe word.',
      'Add your parents to your IDFraud.au Family Protection Shield.',
      'Review their credit report annually to check for unauthorized loans.'
    ],
    relatedSlugs: ['ai-voice-cloning-family-emergency-scams', 'remote-access-scams-teamviewer-anydesk', 'bank-impersonation-fraud-australian-banking']
  },
  {
    id: 'guide-22-teen-identity-theft',
    slug: 'teen-identity-theft-gaming-social-media',
    title: 'Child & Teen Identity Theft: Protecting Gaming Accounts, School Data & First Tax File Numbers',
    category: 'Identity Theft',
    date: '06 July 2026',
    author: {
      name: 'Lachlan Hayes',
      role: 'Identity Security Lead, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Children and teenagers represent "clean slate" identity targets for synthetic identity theft and gaming account takeovers.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Teaching teenagers fundamental digital hygiene when receiving their first Tax File Number.',
    summary: 'Minors have pristine credit files that criminals exploit for synthetic identity fraud. Learn how gaming account compromises occur and how to protect teens entering the Australian workforce.',
    keyTakeaways: [
      'Child identity theft often goes undetected for years until the victim turns 18 and applies for their first loan or mobile phone plan.',
      'Criminals target teen gaming profiles (Roblox, Fortnite, Steam, Discord) with phishing links and token-stealing malware.',
      'When teenagers apply for their first TFN for part-time work, their permanent Australian tax profile becomes active.',
      'Teach young Australians to recognize phishing links and never share two-factor authentication codes.'
    ],
    content: [
      'Child and teenage identity theft is one of the fastest-growing yet least discussed cybercrimes in Australia.',
      'Because children under 18 have clean credit files with zero credit inquiries or debt defaults, their leaked credentials (from school database breaches, sports club registrations, or gaming accounts) are highly prized by synthetic identity fraud rings.',
      'In this article, we explain how child identity theft happens and how parents can protect their children’s digital footprint.'
    ],
    sections: [
      {
        heading: 'Gaming & Social Engineering: The Gateway to Teen ID Theft',
        body: [
          'Teenagers are heavily targeted on platforms like Discord, Roblox, TikTok, and Steam:',
          '1. Fake Free Currency / In-Game Items: Scammers offer "free V-Bucks or Robux" directing teens to phishing sites that harvest account credentials and parental credit card details.',
          '2. Discord Token Stealers: Malicious downloads disguised as game mods or voice changers steal browser session cookies and Discord authentication tokens.',
          '3. Social Engineering for Passwords: Scammers convince teens to share screenshots of their screens, capturing one-time SMS verification codes.'
        ]
      }
    ],
    actionSteps: [
      'Enable Multi-Factor Authentication on all gaming and school accounts.',
      'Store children’s birth certificates and TFN paperwork securely in a home safe.',
      'Talk with teens about recognizing phishing links and suspicious Discord messages.',
      'Add dependent children to IDFraud.au Family Monitoring.'
    ],
    relatedSlugs: ['protecting-elderly-parents-from-digital-scams', 'password-security-passkeys-mfa', 'tax-file-number-tfn-theft-prevention']
  },
  {
    id: 'guide-23-dark-web-monitoring',
    slug: 'dark-web-credential-monitoring-explained',
    title: 'How Dark Web Credential Monitoring Actually Works: Demystifying Breach Intelligence',
    category: 'Data Breaches',
    date: '04 July 2026',
    author: {
      name: 'Marcus Vance',
      role: 'Fraud Intelligence Analyst, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Dark web crawlers monitor illicit hacking forums, Telegram channels, and paste sites for exposed Australian credentials.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Cryptographic k-anonymity matching checks exposures without transmitting plaintext credentials.',
    summary: 'What is the dark web, how do breach detection pipelines discover leaked Australian credentials, and what happens when your data appears on illicit marketplaces?',
    keyTakeaways: [
      'The dark web consists of encrypted overlay networks (primarily Tor) where cybercriminal forums trade stolen database dumps.',
      'Breach intelligence engines monitor illicit forums, private Telegram data-leak channels, and paste sites 24/7.',
      'Privacy-preserving k-anonymity cryptographic hashing allows your identifiers to be checked without exposing your search query to third parties.',
      'An alert on the dark web does not mean you have lost money—it means you have a window of opportunity to rotate passwords before criminals strike.'
    ],
    content: [
      'Marketing campaigns often portray the "dark web" as a mysterious, shadowy realm filled with hacker clichés and movie tropes.',
      'In reality, the dark web is simply a collection of websites hosted on encrypted routing networks (such as Tor) that require specialized software to access. Within this ecosystem, criminal syndicates operate marketplace forums where stolen corporate databases and combo lists are bought and sold.',
      'In this technical explainer, we pull back the curtain on how modern threat intelligence engines crawl these repositories and how IDFraud.au provides early warning alerts.'
    ],
    sections: [
      {
        heading: 'The Lifecycle of Leaked Australian Data',
        body: [
          'Stage 1 — The Corporate Breach: A company’s cloud database or API is compromised by threat actors.',
          'Stage 2 — Private Extortion: The hackers demand a ransomware extortion fee. If the company refuses, the data moves to underground distribution.',
          'Stage 3 — Telegram & Forum Drops: Hackers post samples or full datasets on illicit marketplaces (e.g. BreachForums) and private Telegram leak channels to build criminal reputation.',
          'Stage 4 — Aggregation into "Combo Lists": Automated credential-stuffing tools ingest the leaked emails and passwords to launch brute-force attacks across Australian banking and retail portals.'
        ]
      }
    ],
    actionSteps: [
      'Run an exposure check on IDFraud.au using your email and mobile number.',
      'Rotate passwords immediately whenever a dark web match is identified.',
      'Adopt a password manager to ensure compromised passwords cannot affect other accounts.',
      'Enable continuous dark web surveillance on IDFraud.au.'
    ],
    relatedSlugs: ['password-security-passkeys-mfa', 'how-to-place-a-credit-ban-australia', 'australian-driver-licence-protection-guide']
  },
  {
    id: 'guide-24-romance-investment-scams',
    slug: 'social-engineering-romance-scams-australia',
    title: 'Social Engineering & Romance Investment Scams: How "Pig Butchering" Operations Target Australians',
    category: 'Scams',
    date: '02 July 2026',
    author: {
      name: 'Sarah Chen',
      role: 'Consumer Rights Specialist, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 8,
    imageUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Romance investment scams (Sha Zhu Pan) combine psychological grooming with rigged cryptocurrency trading dashboards.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Never transfer funds to unverified offshore cryptocurrency platforms or individuals met online.',
    summary: 'Romance-baiting investment fraud (known globally as "Pig Butchering" or Sha Zhu Pan) causes astronomical losses in Australia. Learn how grooming operates and how to identify fake trading apps.',
    keyTakeaways: [
      'Scammers initiate contact via "wrong number" WhatsApp texts, dating apps (Tinder, Bumble, Hinge), or LinkedIn professional messages.',
      'They build emotional trust over weeks before casually introducing their "wealthy uncle" or "insider crypto trading signals".',
      'Victims are directed to counterfeit trading platforms that display fake exponential profits and allow small initial withdrawals.',
      'When victims attempt to withdraw large sums, the platform demands exorbitant "tax fees" and "anti-money laundering deposits", freezing the funds.'
    ],
    content: [
      'Romance and investment fraud accounts for the single highest financial loss category reported to Scamwatch, draining hundreds of millions of dollars from Australians annually.',
      'Known in cybersecurity circles as "Pig Butchering" (a translation of the Mandarin term Sha Zhu Pan), these industrial-scale operations are run by transnational criminal syndicates based in Southeast Asian compound facilities.',
      'In this article, we dissect the psychological grooming mechanics and explain why fake trading portals appear so deceptively real.'
    ],
    sections: [
      {
        heading: 'The Psychological Stages of Romance Investment Grooming',
        body: [
          'Stage 1 — The Accidental Text: The scammer sends a polite message asking for "David" or "Sophie" regarding an art exhibition or golf game. When you reply that they have the wrong number, they apologize gracefully and strike up a warm, charming conversation.',
          'Stage 2 — The Cultivation of Trust: Over weeks of daily messaging, the scammer shares photos of luxurious meals, sports cars, and overseas holidays, portraying themselves as a successful entrepreneur.',
          'Stage 3 — The Casual Investment Mention: They mention they are executing a cryptocurrency trade on their phone and offer to teach you with a small amount ($500 AUD).',
          'Stage 4 — The Controlled Environment: The fake trading portal shows your $500 becoming $750 in hours. You successfully withdraw $750 to your real bank account, establishing complete trust.',
          'Stage 5 — The Slaughter: Convinced the platform is real, the victim deposits life savings, redraws home mortgages, or borrows from family ($100,000+ AUD). When they attempt to cash out, the account is frozen, demanding a 25% "liquidity tax", and the scammer vanishes.'
        ]
      }
    ],
    actionSteps: [
      'Never invest money based on recommendations from someone you have only met online.',
      'Check whether the investment platform is licensed on the ASIC Professional Registers (`asic.gov.au`).',
      'Conduct a reverse image search on profile photos using Google Lens.',
      'If you suspect a loved one is being groomed, speak to them gently and contact IDCARE on 1800 595 160.'
    ],
    relatedSlugs: ['cryptocurrency-drainer-recovery-scams', 'protecting-elderly-parents-from-digital-scams', 'job-offer-employment-recruitment-scams']
  },
  {
    id: 'guide-25-travel-scams',
    slug: 'travel-scams-hotel-booking-airline-fraud',
    title: 'Identity Safety While Travelling Abroad: Protecting Australian Passports and Booking Credentials',
    category: 'Australian ID Protection',
    date: '30 June 2026',
    author: {
      name: 'Lachlan Hayes',
      role: 'Identity Security Lead, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Maintain digital and physical identity safeguards when navigating overseas airports, hotels, and public Wi-Fi.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Using travel eSIMs and VPNs prevents rogue Wi-Fi credential interception.',
    summary: 'Australians travelling overseas face unique identity theft risks from public airport Wi-Fi, hotel passport photo demands, and fake booking confirmation scams. Here is your travel safety protocol.',
    keyTakeaways: [
      'Never allow overseas hotels or scooter rental shops to keep your physical Australian passport as a deposit guarantee.',
      'Provide watermarked paper photocopies of your passport instead of surrendering original documents.',
      'Avoid unencrypted public airport and cafe Wi-Fi networks unless using a trusted VPN and encrypted cellular eSIM.',
      'Beware of Booking.com in-app phishing messages originating from compromised hotel partner accounts.'
    ],
    content: [
      'Australians are avid international travellers, heading overseas in millions to Bali, Europe, Japan, and the Americas.',
      'However, being in unfamiliar surroundings, relying on public Wi-Fi, and routinely handing over identification to foreign hotels and car rentals creates heightened vulnerability for identity compromise.',
      'In this travel security playbook, we outline the fundamental rules to keep your Australian credentials safe while exploring the world.'
    ],
    sections: [
      {
        heading: 'Passport Handling Protocol for International Travel',
        body: [
          '1. Never Surrender Your Physical Passport as Collateral: In many countries, hotel staff or rental operators ask to retain your passport in their drawer. Politely decline and provide a pre-printed, watermarked photocopy.',
          '2. Watermark Digital Copies: Use the IDFraud.au Watermark Tool to create stamped copies: "COPY — FOR HOTEL REGISTRATION AT [HOTEL] ONLY".',
          '3. Secure Storage: When exploring, carry a watermarked copy and keep your physical passport locked inside your hotel room digital safe.',
          '4. Keep Digital Backups on a Encrypted Cloud: Store high-res copies of your passport bio-page and emergency consular contacts on an encrypted drive accessible via your smartphone.'
        ]
      }
    ],
    actionSteps: [
      'Print watermarked passport copies using the IDFraud.au Watermark Tool before departure.',
      'Register your overseas travel itinerary on Smartraveller (`smartraveller.gov.au`).',
      'Use a privacy-respecting VPN and travel eSIM for internet access abroad.',
      'Save the 24/7 DFAT Consular Emergency Centre number (+61 2 6261 3305).'
    ],
    relatedSlugs: ['australian-passport-lost-stolen-fraud-response', 'how-to-place-a-credit-ban-australia', 'online-shopping-scams-fake-storefronts-australia']
  },
  {
    id: 'guide-26-crypto-drainers',
    slug: 'cryptocurrency-drainer-recovery-scams',
    title: 'Crypto Phishing Drainers & "Asset Recovery" Secondary Scams: How to Avoid Double Losses',
    category: 'Scams',
    date: '28 June 2026',
    author: {
      name: 'Marcus Vance',
      role: 'Fraud Intelligence Analyst, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1621416894569-0f39ed31d247?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Malicious Web3 smart contract approvals can drain digital wallets in a single transaction.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Beware of "fund recovery specialists" claiming they can hack stolen cryptocurrency back.',
    summary: 'How malicious smart contract approval drainers work, and why fake "crypto recovery specialists" who claim they can hack back stolen funds are secondary scams designed to defraud victims twice.',
    keyTakeaways: [
      'Malicious Web3 drainers trick wallet owners into signing `setApprovalForAll` or `permit` transactions that allow smart contracts to sweep all assets.',
      'Never input your 12 or 24-word seed phrase or private key into any website, form, or Discord message.',
      'Secondary "Asset Recovery Scams": Fraudulent firms claiming they can "retrieve stolen cryptocurrency" are 100% scams demanding upfront retainer fees.',
      'Once cryptocurrency is transferred on a decentralized blockchain, it cannot be reversed by third-party private hackers.'
    ],
    content: [
      'The intersection of cryptocurrency, decentralized finance (DeFi), and phishing has created devastating financial losses for Australian digital asset holders.',
      'Compounding the tragedy, victims who seek assistance online are frequently targeted by secondary "Recovery Scammers" claiming they possess specialized forensic tools to hack back stolen funds.',
      'In this article, we explain the technical mechanisms of crypto drainers and provide transparent, factual advice on dealing with digital asset fraud.'
    ],
    sections: [
      {
        heading: 'How Smart Contract Drainers Steal Wallet Funds',
        body: [
          'Drainers operate through deceptive signature requests on fake minting sites, NFT airdrops, or decentralized exchange clones:',
          'When you click "Connect Wallet" and confirm a prompt, you are not signing a simple login; you are signing a transaction granting the malicious contract unlimited spending allowance over your tokens.',
          'The contract executes an automated sweep in milliseconds, transferring all Ethereum, Bitcoin, Solana, and stablecoins into mixer contracts.'
        ]
      },
      {
        heading: 'The Secondary Recovery Scam: Defrauding Victims Twice',
        body: [
          'If you post on Reddit, X (Twitter), or Facebook about losing money to a scam, you will receive dozens of direct messages recommending an "ethical hacker on Telegram or Instagram" who "recovered their funds in 24 hours".',
          'These recovery agents are the exact same criminal syndicates operating under new aliases. They produce fake terminal screenshots showing your funds in an "escrow wallet" and demand an upfront fee of $2,000 to $10,000 AUD to release the transfer, vanishing the moment the fee is paid.'
        ]
      }
    ],
    actionSteps: [
      'Never share your wallet private keys or 12-word seed phrases with anyone.',
      'Revoke suspicious smart contract allowances regularly on `revoke.cash`.',
      'Never pay upfront fees to private "crypto recovery hackers".',
      'Report cryptocurrency fraud to ReportCyber (`cyber.gov.au`) and your Australian crypto exchange.'
    ],
    relatedSlugs: ['social-engineering-romance-scams-australia', 'job-offer-employment-recruitment-scams', 'comprehensive-identity-theft-recovery-playbook']
  },
  {
    id: 'guide-27-credit-repair-vs-afca',
    slug: 'credit-repair-agencies-vs-free-disputes-afca',
    title: 'Credit Repair Companies vs Free AFCA Disputes: How to Remove Fraudulent Debts for Free',
    category: 'Credit Fraud',
    date: '26 June 2026',
    author: {
      name: 'Sarah Chen',
      role: 'Consumer Rights Specialist, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1450133064473-71024230f91b?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'The Australian Financial Complaints Authority (AFCA) provides free, legally binding dispute resolution for consumers.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'You do not need to pay thousands of dollars to commercial credit repair firms to fix fraudulent defaults.',
    summary: 'Commercial credit repair companies charge thousands of dollars for dispute processes you can complete yourself for free under Australian law. Learn how AFCA and credit bureau dispute rules protect you.',
    keyTakeaways: [
      'Commercial credit repair agencies often charge between $1,500 and $4,000 AUD with no guarantee of success.',
      'Under the Privacy Act and National Consumer Credit Protection Act, disputing fraudulent listings with credit bureaus is 100% free.',
      'The Australian Financial Complaints Authority (AFCA) offers free, independent dispute resolution with binding power over Australian lenders.',
      'Lenders and credit bureaus must investigate and resolve identity theft credit disputes within 30 statutory days.'
    ],
    content: [
      'When an Australian discovers fraudulent default notices or unauthorized loans on their credit file after an identity theft incident, their immediate reaction is panic.',
      'Predatory commercial "credit repair" companies advertise heavily on search engines, promising to "clean your credit score" in exchange for hefty upfront fees ($1,500 to $4,500 AUD).',
      'In this consumer rights guide, we explain why you should never pay a credit repair company and provide the free, legally enforceable dispute roadmap.'
    ],
    sections: [
      {
        heading: 'Your Free Statutory Dispute Rights in Australia',
        body: [
          'Under Part IIIA of the Commonwealth Privacy Act 1988:',
          '1. Free Direct Bureau Disputes: You have the legal right to lodge a correction request directly with Equifax, Experian, or illion at zero cost. If the entry resulted from identity theft (supported by a police event number or IDCARE report), the bureau must investigate and correct the listing within 30 days.',
          '2. Internal Dispute Resolution (IDR): You can submit a dispute to the lender’s internal fraud department. Australian credit licensees must respond in writing within 21 to 30 days.',
          '3. External Dispute Resolution via AFCA: If the lender fails to resolve the issue fairly, you can lodge a free complaint with the Australian Financial Complaints Authority (AFCA on 1800 931 678). AFCA’s decisions are legally binding on lenders.'
        ]
      }
    ],
    actionSteps: [
      'Request your free credit report from Equifax, Experian, and illion.',
      'Submit free dispute notices directly to credit reporting bodies with your police event number.',
      'Lodge a free complaint with AFCA (`afca.org.au`) if lenders refuse to waive fraudulent debts.',
      'Never sign contracts or pay retainer fees to commercial credit repair brokers.'
    ],
    relatedSlugs: ['how-to-place-a-credit-ban-australia', 'australian-driver-licence-protection-guide', 'comprehensive-identity-theft-recovery-playbook']
  },
  {
    id: 'guide-28-data-brokers-opt-out',
    slug: 'data-broker-opt-out-privacy-minimisation',
    title: 'How to Remove Your Personal Data from Australian People-Search Engines & Data Brokers',
    category: 'Data Breaches',
    date: '24 June 2026',
    author: {
      name: 'Lachlan Hayes',
      role: 'Identity Security Lead, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Shrinking your digital footprint reduces the publicly available OSINT data used in spear-phishing attacks.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Submitting statutory opt-out requests under the Australian Privacy Principles (APPs).',
    summary: 'Data aggregators, marketing brokers, and people-search sites compile detailed dossiers on Australian residents. Learn how to opt out, remove public listings, and minimize your digital attack surface.',
    keyTakeaways: [
      'Data brokers scrape public electoral rolls, real estate transaction records, phone directories, and social media to construct consumer profiles.',
      'Scammers utilize open-source intelligence (OSINT) from data aggregators to personalize phishing attacks and answer security questions.',
      'Under Australian Privacy Principle 13, you have the right to request correction or deletion of inaccurate and outdated personal data.',
      'Remove your name and address from online directories (e.g. White Pages AU) and opt out of direct marketing lists.'
    ],
    content: [
      'Have you ever wondered how scammers know your home address, past phone numbers, or property purchase history?',
      'The answer lies in the sprawling commercial data broker industry. These data aggregators collect, correlate, and monetize personal information from public government registers, real estate sale portals, telephone directories, and online sweepstakes.',
      'In this tactical privacy guide, we provide the step-by-step instructions to systematically opt out and remove your data from major Australian aggregators.'
    ],
    sections: [
      {
        heading: 'Key Australian Data Directories to Opt Out From',
        body: [
          '1. White Pages Australia: Search your name on `whitepages.com.au`. If your residential address or mobile number appears, submit an unlisting request to remove your entry from public search.',
          '2. Real Estate Sale Portals: Contact property websites (e.g. Domain, Realestate.com.au, OnTheHouse) to request that historical sold prices and interior photos of your current home be suppressed.',
          '3. Direct Marketing Association (ADMA) Do Not Mail: Register your name on the ADMA Do Not Mail and Do Not Call register to block unsolicited marketing databases.'
        ]
      }
    ],
    actionSteps: [
      'Submit unlisting requests to White Pages Australia and online directories.',
      'Register your mobile and landline numbers on the Australian Do Not Call Register (`donotcall.gov.au`).',
      'Audit your social media privacy settings to prevent public search engine indexing.',
      'Run an identity exposure search on IDFraud.au to detect data leaks.'
    ],
    relatedSlugs: ['password-security-passkeys-mfa', 'dark-web-credential-monitoring-explained', 'rental-application-id-theft-watermarking']
  },
  {
    id: 'guide-29-2fa-comparison',
    slug: 'two-factor-authentication-comparison-sms-totp-passkeys',
    title: 'SMS 2FA vs Authenticator Apps vs Hardware Security Keys: What Every Australian Needs to Know',
    category: 'Password Security',
    date: '22 June 2026',
    author: {
      name: 'Lachlan Hayes',
      role: 'Identity Security Lead, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 8,
    imageUrl: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Hardware security keys and biometric passkeys offer the highest level of phishing protection available.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1563986768711-b3bde3d801e2?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Comparing time-based one-time passcodes (TOTP) against traditional SMS text messages.',
    summary: 'A deep technical and practical comparison between SMS text codes, Authenticator Apps (2FAS, Google Authenticator), Biometric Passkeys, and YubiKeys for Australian users.',
    keyTakeaways: [
      'SMS 2FA is vulnerable to SIM-swapping, SS7 telecom interception, and real-time phishing reverse-proxies.',
      'Authenticator apps (TOTP) generate time-based codes locally on your smartphone hardware without transmitting cellular data.',
      'Passkeys and FIDO2 Hardware Keys (YubiKey) provide cryptographic origin-binding, making them completely immune to man-in-the-middle phishing.',
      'Prioritize Passkeys for primary email accounts, banking, and myGov.'
    ],
    content: [
      'Multi-Factor Authentication (MFA) is universally recommended by cybersecurity experts and the Australian Cyber Security Centre (ACSC). However, not all MFA methods are created equal.',
      'Understanding the strengths, vulnerabilities, and usability tradeoffs of each authentication method allows you to deploy the right defenses where they matter most.',
      'In this guide, we provide a comprehensive technical comparison across all major 2FA methods used in Australia.'
    ],
    sections: [
      {
        heading: 'Detailed Authentication Comparison Matrix',
        body: [
          'SMS One-Time Passcodes: Convenient, but susceptible to SIM-swapping and social engineering. Best used only as a last resort when no other 2FA option exists.',
          'Authenticator Apps (TOTP): Offline, secure against SIM-swapping, and widely supported across 99% of platforms (Google, Microsoft, 2FAS, Bitwarden).',
          'Biometric Passkeys & FIDO2 WebAuthn: Hardware-bound, impossible to phish, eliminates passwords entirely. The gold standard for modern identity security.'
        ]
      }
    ],
    actionSteps: [
      'Audit your primary accounts using the IDFraud.au Account Security Audit tool.',
      'Upgrade from SMS 2FA to an Authenticator App on primary email and password managers.',
      'Activate Passkeys wherever supported (Google, Apple, Microsoft, myGov).',
      'Store emergency backup recovery codes in a secure offline location.'
    ],
    relatedSlugs: ['password-security-passkeys-mfa', 'sim-swapping-mobile-porting-fraud-australia', 'detecting-mygov-ato-phishing']
  },
  {
    id: 'guide-30-recovery-playbook',
    slug: 'comprehensive-identity-theft-recovery-playbook',
    title: 'The Complete 30-Day Australian Identity Theft Recovery Checklist: From Crisis to Total Security',
    category: 'Identity Theft',
    date: '20 June 2026',
    author: {
      name: 'Sarah Chen',
      role: 'Consumer Rights Specialist, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 9,
    imageUrl: 'https://images.unsplash.com/photo-1450133064473-71024230f91b?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'A methodical 30-day recovery checklist restores legal and financial identity integrity after a major breach.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Coordinating with IDCARE, Equifax, ReportCyber, and state road transport registries.',
    summary: 'The master recovery blueprint for any Australian facing active identity theft or severe breach exposure. Follow this structured timeline over 30 days to systematically restore your security.',
    keyTakeaways: [
      'Day 1–2 (Containment): Secure primary email, freeze compromised bank cards, and place a 21-day Equifax credit ban.',
      'Day 3–7 (Remediation): Order replacement Driver Licence and Medicare cards with new Card Numbers, lodge police reports on ReportCyber.',
      'Day 8–14 (Dispute & Hardening): Contact IDCARE (1800 595 160) for case management, dispute fraudulent credit inquiries, apply ATO security locks.',
      'Day 15–30 (Monitoring & Closure): Extend credit bans to 12 months, audit credit files, and activate 24/7 continuous identity monitoring on IDFraud.au.'
    ],
    content: [
      'Experiencing identity theft can feel overwhelming, disorienting, and violating. Victims are often unsure where to start, who to call, or how to undo the damage.',
      'However, identity recovery is not a chaotic mystery; it is a structured, methodical process. By executing actions in the correct chronological order, you can systematically contain the breach, invalidate stolen documents, erase fraudulent debts, and insulate yourself from future attacks.',
      'This 30-day master recovery playbook is the definitive roadmap for Australian citizens and residents.'
    ],
    sections: [
      {
        heading: 'The 30-Day Chronological Recovery Blueprint',
        body: [
          'Phase 1: Days 1 to 2 — Emergency Lockdown: Change primary email passwords, force sign-out across all devices, enable app-based 2FA, contact bank fraud teams to cancel compromised cards, and place an immediate credit ban with Equifax (authorizing notification to Experian and illion).',
          'Phase 2: Days 3 to 7 — Invalidate Stolen Credentials: Order replacement physical driver licence and Medicare cards to generate new Card Numbers on the Document Verification Service (DVS). File an official cybercrime report on `cyber.gov.au` to obtain a ReportCyber incident number.',
          'Phase 3: Days 8 to 14 — Case Management & Bureau Clean-up: Call IDCARE (1800 595 160) for free case management. Contact the ATO Client Identity Support Centre (1800 467 033) to apply tax security flags. Review your statutory credit reports and dispute unauthorized credit inquiries.',
          'Phase 4: Days 15 to 30 — Long-Term Hardening: Submit your police report to credit bureaus to extend your credit ban for 12 months for free. Register for 24/7 continuous Dark Web surveillance and document monitoring on IDFraud.au.'
        ]
      }
    ],
    actionSteps: [
      'Launch the IDFraud.au 8-Step Emergency Recovery Wizard to track your recovery progress interactively.',
      'Place a 21-day temporary credit ban across Equifax, Experian, and illion.',
      'Obtain a police event number from ReportCyber (`cyber.gov.au`).',
      'Call IDCARE on 1800 595 160 for free national identity case support.',
      'Activate 24/7 ongoing protection on IDFraud.au.'
    ],
    relatedSlugs: ['how-to-place-a-credit-ban-australia', 'australian-driver-licence-protection-guide', 'medicare-card-compromise-replacement-guide']
  }
];
